import { useMutation } from '@apollo/client';
import type { InsertUserGroupMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertUserGroupDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertUserGroupTRPC } from './useInsertUserGroupTRPC';

type InsertUserGroupInput = {
  Name: string;
  Email?: string | null;
  Description?: string | null;
  OwnerContributor?: boolean;
};

export const useInsertUserGroup = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertUserGroupGraphQL, graphqlState] = useMutation(
    InsertUserGroupDocument,
    {
      update: (cache) => {
        evictField(cache, 'user_group_user');
        evictField(cache, 'user_group');
        evictField(cache, 'user');
      },
    }
  );

  const trpcMutation = useInsertUserGroupTRPC();

  const insertUserGroup = async (
    variables: InsertUserGroupInput
  ): Promise<InsertUserGroupMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertUserGroup(variables);
    }

    const result = await insertUserGroupGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert user group');
    }

    return result.data;
  };

  return {
    insertUserGroup,
    loading: trpcEnabled ? trpcMutation.loading : graphqlState.loading,
    error: trpcEnabled ? trpcMutation.error : graphqlState.error,
  };
};
